import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtT, fmtN } from "./lib";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Search, Lock, CheckCircle2, Clock, ShieldCheck, X,
} from "lucide-react";
import { toast } from "sonner";

export default function GranosCierre({ user, canAuthorize }) {
  const email = (user?.email || "").toLowerCase();
  const isIngrid = email === "ingrid.bayer@tecnomyl.com";

  const [rows, setRows] = useState([]);
  const [deps, setDeps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productor, setProductor] = useState("");
  const [sel, setSel] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);

  const loadContratos = useCallback(async () => {
    setLoading(true);
    try {
      const params = productor.trim() ? { productor: productor.trim() } : {};
      const res = await api.get("/granos/cierre/contratos", { params });
      setRows(res.data.contratos || []);
      setDeps(res.data.depositos || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar contratos");
    } finally { setLoading(false); }
  }, [productor]);

  const loadSolicitudes = useCallback(async () => {
    if (!canAuthorize) return;
    try {
      const res = await api.get("/granos/cierre/solicitudes");
      setSolicitudes(res.data.solicitudes || []);
    } catch { /* ignore */ }
  }, [canAuthorize]);

  useEffect(() => { const t = setTimeout(loadContratos, 250); return () => clearTimeout(t); }, [loadContratos]);
  useEffect(() => { loadSolicitudes(); }, [loadSolicitudes]);

  const onApplied = () => { setSel(null); loadContratos(); loadSolicitudes(); };

  const decide = async (code, aprobar) => {
    try {
      await api.post("/granos/cierre/autorizar", { code, aprobar });
      toast.success(aprobar ? "Cierre autorizado y aplicado" : "Solicitud rechazada");
      onApplied();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo procesar");
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="granos-cierre-tab">
      {isIngrid && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
          Tus cierres requieren la autorización de Bruno. Se le avisará por WhatsApp y en su app.
        </div>
      )}

      {canAuthorize && solicitudes.length > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 overflow-hidden" data-testid="granos-solicitudes">
          <div className="bg-[#0f5233] text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4 text-lime-300" /> Solicitudes por autorizar ({solicitudes.length})
          </div>
          <div className="divide-y">
            {solicitudes.map((s) => (
              <div key={s.code} className="p-3 space-y-2" data-testid={`granos-solicitud-${s.code}`}>
                <p className="text-[13px] text-slate-700 whitespace-pre-line">{s.resumen}</p>
                <p className="text-[11px] text-slate-400">Código {s.code} · Solicita {s.initiator_name}</p>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#0f5233] hover:bg-[#0b3f27]" data-testid={`granos-auth-approve-${s.code}`} onClick={() => decide(s.code, true)}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Autorizar
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-red-600 border-red-200" data-testid={`granos-auth-reject-${s.code}`} onClick={() => decide(s.code, false)}>
                    <X className="h-4 w-4 mr-1" /> Rechazar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input value={productor} onChange={(e) => setProductor(e.target.value)} placeholder="Buscar cliente para cerrar..."
          className="h-11 w-full rounded-xl border border-slate-200 pl-9 pr-3 text-sm" data-testid="granos-cierre-search" />
      </div>

      <p className="text-xs text-slate-400">{rows.length} contrato(s) con volumen sin costo y precio</p>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0f5233]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400">No hay contratos pendientes de cierre.</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((c) => (
            <button key={c.cid} onClick={() => setSel(c)} data-testid={`granos-cierre-card-${c.contrato}`}
              className="w-full text-left rounded-2xl border bg-white px-4 py-3 active:bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{c.productor}</p>
                  <p className="text-[11px] text-slate-400">Ctr {c.contrato} · {c.cultivo}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-[#0f5233] leading-none">{fmtT(c.sin_costo_precio)}</p>
                  <p className="text-[10px] text-slate-400">t sin C/P</p>
                </div>
              </div>
              <div className="mt-1.5 flex gap-2 text-[11px]">
                {c.precio_cerrado != null && <span className="rounded bg-emerald-100 text-emerald-700 px-1.5 py-0.5">Precio ✓</span>}
                {c.costo_cerrado != null && <span className="rounded bg-teal-100 text-teal-700 px-1.5 py-0.5">Costo ✓</span>}
                <span className="text-slate-400">Precio actual {fmtN(c.precio)} · Costo {fmtN(c.costo)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <CierreDialog contrato={sel} deps={deps} onClose={() => setSel(null)} onApplied={onApplied} isIngrid={isIngrid} />
    </div>
  );
}

function CierreDialog({ contrato, deps, onClose, onApplied, isIngrid }) {
  const [modo, setModo] = useState("ambos");
  const [precio, setPrecio] = useState("");
  const [costo, setCosto] = useState("");
  const [tn, setTn] = useState("");
  const [lugar, setLugar] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (contrato) {
      setModo("ambos"); setPrecio(""); setCosto("");
      setTn(String(contrato.sin_costo_precio || "")); setLugar("");
    }
  }, [contrato]);

  if (!contrato) return null;
  const num = (s) => { const v = parseFloat(String(s).replace(/\./g, "").replace(",", ".")); return isNaN(v) ? null : v; };

  const submit = async () => {
    const body = { cid: contrato.cid };
    const tnv = num(tn);
    if (modo === "precio" || modo === "ambos") {
      const p = num(precio);
      if (p == null) { toast.error("Ingresá el precio"); return; }
      body.precio = p; body.precio_tn = tnv;
    }
    if (modo === "costo" || modo === "ambos") {
      const c = num(costo);
      if (c == null) { toast.error("Ingresá el costo"); return; }
      body.costo = c; body.costo_tn = tnv;
    }
    if (contrato.needs_lugar && lugar.trim()) body.lugar_entrega = lugar.trim();
    setBusy(true);
    try {
      const res = await api.post("/granos/cierre/aplicar", body);
      if (res.data.pending) toast.success(`Solicitud enviada a Bruno (código ${res.data.code})`);
      else toast.success("Cierre aplicado y actualizado");
      onApplied();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo aplicar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!contrato} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="granos-cierre-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-[#0f5233]" /> Cerrar precio / costo</DialogTitle>
          <DialogDescription>{contrato.productor} · Ctr {contrato.contrato} [{contrato.cultivo}] · {fmtT(contrato.sin_costo_precio)} t sin C/P</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[["ambos", "Ambos"], ["precio", "Solo precio"], ["costo", "Solo costo"]].map(([k, l]) => (
              <button key={k} onClick={() => setModo(k)} data-testid={`granos-cierre-modo-${k}`}
                className={`rounded-lg py-2 text-xs font-medium border ${modo === k ? "bg-[#0f5233] text-white border-[#0f5233]" : "bg-white text-slate-600 border-slate-200"}`}>
                {l}
              </button>
            ))}
          </div>

          {(modo === "precio" || modo === "ambos") && (
            <div className="space-y-1">
              <Label>Precio (U$D/t)</Label>
              <Input inputMode="decimal" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder="Ej: 385" data-testid="granos-cierre-precio" className="h-11" />
            </div>
          )}
          {(modo === "costo" || modo === "ambos") && (
            <div className="space-y-1">
              <Label>Costo (U$D/t)</Label>
              <Input inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} placeholder="Ej: -20" data-testid="granos-cierre-costo" className="h-11" />
            </div>
          )}
          <div className="space-y-1">
            <Label>Toneladas</Label>
            <Input inputMode="decimal" value={tn} onChange={(e) => setTn(e.target.value)} placeholder="Toneladas a cerrar" data-testid="granos-cierre-tn" className="h-11" />
          </div>
          {contrato.needs_lugar && (
            <div className="space-y-1">
              <Label>Lugar de entrega</Label>
              <Input value={lugar} onChange={(e) => setLugar(e.target.value)} placeholder="Depósito o 'A retirar'" data-testid="granos-cierre-lugar" className="h-11" list="granos-deps" />
              <datalist id="granos-deps">{deps.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0f5233] hover:bg-[#0b3f27] gap-2" data-testid="granos-cierre-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {isIngrid ? "Enviar a autorizar" : "Aplicar cierre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
